// ════════════════════════════════════════════════════════════
// ConfigERP — datos fiscales, series, IVA y formas de pago
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tabs, Tab, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import {
  getConfigERP, actualizarEmpresa, crearSerie, actualizarFormaPago, crearFormaPago
} from '../api';

export default function ConfigERP() {
  const [tab, setTab] = useState(0);
  const [config, setConfig] = useState<any>(null);
  const [empresa, setEmpresa] = useState<any>({});
  const [msg, setMsg] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [serieDialog, setSerieDialog] = useState<any>(null);
  const [pagoDialog, setPagoDialog] = useState<any>(null);
  const [pagosEditados, setPagosEditados] = useState<Record<string, any>>({});

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const esAdmin = user.rol === 'admin';

  const cargar = () => {
    getConfigERP().then((d: any) => {
      setConfig(d);
      setEmpresa(d.empresa || {});
      setPagosEditados({});
    });
  };
  useEffect(() => { cargar(); }, []);

  const avisar = (texto: string) => { setMsg(texto); setTimeout(() => setMsg(''), 4000); };

  const guardarEmpresa = async () => {
    setGuardando(true);
    try {
      await actualizarEmpresa(empresa);
      avisar('Datos fiscales guardados');
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const guardarSerie = async () => {
    try {
      await crearSerie(serieDialog);
      setSerieDialog(null);
      avisar('Serie creada');
      cargar();
    } catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  const guardarPago = async (clave: string) => {
    const p = pagosEditados[clave];
    try {
      await actualizarFormaPago(clave, p);
      avisar('Forma de pago actualizada');
      cargar();
    } catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  const crearPago = async () => {
    try {
      await crearFormaPago(pagoDialog);
      setPagoDialog(null);
      avisar('Forma de pago creada');
      cargar();
    } catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  if (!config) return <CircularProgress />;

  const campo = (clave: string, etiqueta: string, ancho: number = 6) => (
    <Grid item xs={12} sm={ancho}>
      <TextField fullWidth size="small" label={etiqueta}
        value={empresa[clave] || ''}
        onChange={(e) => setEmpresa({ ...empresa, [clave]: e.target.value })}
        disabled={!esAdmin} />
    </Grid>
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1.5rem', md: '2rem' } }}>
        ⚙️ Configuración de facturación
      </Typography>

      {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }} variant="scrollable" scrollButtons="auto">
        <Tab label="🏢 Datos fiscales" />
        <Tab label="🔢 Series" />
        <Tab label="💶 Tipos de IVA" />
        <Tab label="📅 Formas de pago" />
      </Tabs>

      {/* DATOS FISCALES */}
      {tab === 0 && (
        <Card sx={{ borderRadius: 3, maxWidth: 800 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Estos datos aparecen como emisor en todas las facturas. Compruébalos con
              cuidado: una vez emitida una factura, no se pueden corregir en ella.
            </Typography>
            {!esAdmin && <Alert severity="info" sx={{ mb: 2 }}>Solo un administrador puede modificarlos.</Alert>}
            <Grid container spacing={2}>
              {campo('razon_social', 'Razón social', 8)}
              {campo('nif', 'NIF / CIF', 4)}
              {campo('direccion', 'Dirección', 8)}
              {campo('cp', 'Código postal', 4)}
              {campo('ciudad', 'Ciudad', 6)}
              {campo('provincia', 'Provincia', 6)}
              {campo('telefono', 'Teléfono', 6)}
              {campo('email', 'Email', 6)}
              {campo('web', 'Web', 6)}
              {campo('iban', 'IBAN (para el cobro)', 6)}
              {campo('registro_mercantil', 'Registro mercantil', 12)}
            </Grid>
            {esAdmin && (
              <Button variant="contained" startIcon={<SaveIcon />} onClick={guardarEmpresa}
                disabled={guardando} sx={{ mt: 2, bgcolor: '#4f46e5' }}>
                Guardar datos fiscales
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* SERIES */}
      {tab === 1 && (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            La numeración debe ser correlativa y sin huecos. El sistema la gestiona
            automáticamente y reinicia el contador cada ejercicio.
          </Alert>
          {esAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} sx={{ mb: 2, bgcolor: '#4f46e5' }}
              onClick={() => setSerieDialog({ clave: '', prefijo: '', descripcion: '',
                tipo_documento: 'factura_emitida', ultimo_numero: 0 })}>
              Nueva serie
            </Button>
          )}
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Serie</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tipo de documento</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ejercicio</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Último número</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {config.series.map((s: any) => (
                  <TableRow key={s.clave}>
                    <TableCell sx={{ fontWeight: 700 }}>{s.prefijo}</TableCell>
                    <TableCell>
                      <Chip size="small" label={
                        s.tipo_documento === 'factura_emitida' ? 'Factura emitida' :
                        s.tipo_documento === 'pedido_compra' ? 'Pedido de compra' : 'Factura recibida'
                      } />
                    </TableCell>
                    <TableCell>{s.descripcion || '—'}</TableCell>
                    <TableCell>{s.ejercicio}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {s.ultimo_numero === 0 ? 'Sin usar' : `${s.prefijo}${s.ejercicio}/${String(s.ultimo_numero).padStart(4, '0')}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* TIPOS DE IVA */}
      {tab === 2 && (
        <TableContainer component={Paper} sx={{ borderRadius: 3, maxWidth: 600 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Porcentaje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {config.tipos_iva.map((i: any) => (
                <TableRow key={i.clave}>
                  <TableCell>{i.nombre}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{i.porcentaje}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* FORMAS DE PAGO */}
      {tab === 3 && (
        <Box>
<Button variant="contained" startIcon={<AddIcon />} sx={{ mb: 2, bgcolor: '#4f46e5' }}
            onClick={() => setPagoDialog({ clave: '', nombre: '', dias_vencimiento: 30, n_vencimientos: 1 })}>
            Nueva forma de pago
          </Button>
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 150 }}>Días</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 150 }}>Nº vencimientos</TableCell>
                  <TableCell sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {config.formas_pago.map((p: any) => {
                  const ed = pagosEditados[p.clave];
                  return (
                    <TableRow key={p.clave}>
                      <TableCell>
                        <TextField variant="standard" fullWidth
                          value={ed?.nombre ?? p.nombre}
                          onChange={(e) => setPagosEditados({ ...pagosEditados,
                            [p.clave]: { ...p, ...ed, nombre: e.target.value } })} />
                      </TableCell>
                      <TableCell>
                        <TextField variant="standard" type="number"
                          value={ed?.dias_vencimiento ?? p.dias_vencimiento}
                          onChange={(e) => setPagosEditados({ ...pagosEditados,
                            [p.clave]: { ...p, ...ed, dias_vencimiento: e.target.value } })} />
                      </TableCell>
                      <TableCell>
                        <TextField variant="standard" type="number"
                          value={ed?.n_vencimientos ?? p.n_vencimientos}
                          onChange={(e) => setPagosEditados({ ...pagosEditados,
                            [p.clave]: { ...p, ...ed, n_vencimientos: e.target.value } })} />
                      </TableCell>
                      <TableCell>
                        {ed && (
                          <Button size="small" onClick={() => guardarPago(p.clave)}>Guardar</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* DIALOG NUEVA SERIE */}
      <Dialog open={!!serieDialog} maxWidth="xs" fullWidth
        onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setSerieDialog(null); }}>
        <DialogTitle>Nueva serie</DialogTitle>
        <DialogContent>
          {serieDialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Clave (ej. FV2)" value={serieDialog.clave}
                onChange={(e) => setSerieDialog({ ...serieDialog, clave: e.target.value.toUpperCase() })} />
              <TextField label="Prefijo en el número" value={serieDialog.prefijo}
                onChange={(e) => setSerieDialog({ ...serieDialog, prefijo: e.target.value.toUpperCase() })} />
              <FormControl fullWidth>
                <InputLabel>Tipo de documento</InputLabel>
                <Select value={serieDialog.tipo_documento} label="Tipo de documento"
                  onChange={(e) => setSerieDialog({ ...serieDialog, tipo_documento: e.target.value })}>
                  <MenuItem value="factura_emitida">Factura emitida</MenuItem>
                  <MenuItem value="pedido_compra">Pedido de compra</MenuItem>
                  <MenuItem value="factura_recibida">Factura recibida</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Descripción" value={serieDialog.descripcion}
                onChange={(e) => setSerieDialog({ ...serieDialog, descripcion: e.target.value })} />
              <TextField label="Último número usado" type="number" value={serieDialog.ultimo_numero}
                helperText="Si vienes de otro programa, pon aquí el último número emitido."
                onChange={(e) => setSerieDialog({ ...serieDialog, ultimo_numero: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSerieDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarSerie} sx={{ bgcolor: '#4f46e5' }}>Crear</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG NUEVA FORMA DE PAGO */}
      <Dialog open={!!pagoDialog} maxWidth="xs" fullWidth
        onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setPagoDialog(null); }}>
        <DialogTitle>Nueva forma de pago</DialogTitle>
        <DialogContent>
          {pagoDialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Nombre (ej. 45 días fecha factura)" value={pagoDialog.nombre}
                onChange={(e) => setPagoDialog({ ...pagoDialog, nombre: e.target.value,
                  clave: e.target.value.toLowerCase().replace(/\s+/g, '_').slice(0, 20) })} />
              <TextField label="Días hasta el vencimiento" type="number" value={pagoDialog.dias_vencimiento}
                onChange={(e) => setPagoDialog({ ...pagoDialog, dias_vencimiento: e.target.value })} />
              <TextField label="Número de vencimientos" type="number" value={pagoDialog.n_vencimientos}
                helperText="1 = pago único. 3 con 30 días = 30/60/90."
                onChange={(e) => setPagoDialog({ ...pagoDialog, n_vencimientos: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagoDialog(null)}>Cancelar</Button>
          <Button variant="contained" onClick={crearPago} sx={{ bgcolor: '#4f46e5' }}>Crear</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}